import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { FILE_SCOPE_USER } from '../../../../constants/file'
import { SIZE } from '../../../../constants/size'
import { Field } from '../../../Field'
import { FileList, FilePreview, useFileUpload } from '../../../File'
import {
  FilesPreviewGallery,
  useFilePreviewGallery,
} from '../../../FilesPreviewGallery'
import {
  FormBody,
  FormFieldsContainer,
  FormInlineNotifications,
  useFormValidationErrorsNotification,
} from '../../../Form'
import { Content } from '../../../Modal'

import { AddressField } from './AddressField'
import { BuildingField } from './BuildingField'
import { CategoryField } from './CategoryField'
import { DescriptionField } from './DescriptionField'
import { EmailField } from './EmailField'
import { NameField } from './NameField'
import { OpeningHoursField } from './OpeningHoursField'
import { PhoneField } from './PhoneField'

import * as styles from './FormFields.module.css'

function FormFields({
  ContentComponent = Content,
  contentComponentProps: {
    size: contentSize = SIZE.X_LARGE,
    ...contentComponentProps
  } = EMPTY_OBJECT,
  files,
  notifications,
  setFiles,
  onCancel,
  onRemoveNotification,
  onSetNotification,
}) {
  const { dirty, isSubmitting } = useFormikContext()

  const { t } = useTranslation()

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({ files })

  const {
    filesError,
    filesUploading,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files,
    onSetFiles: setFiles,
  })

  useFormValidationErrorsNotification({
    files: { filesError, filesUploading },
    onRemoveNotification,
    onSetNotification,
  })

  function handleCancel() {
    return onCancel({ dirty, files })
  }

  function renderFile(file, key) {
    return (
      <FilePreview
        disabled={isSubmitting}
        file={file}
        key={key}
        onClick={handleClickFile}
        onRemoveFile={handleRemoveFile}
      />
    )
  }

  const showFiles =
    files?.length > 0 || filesUploading?.length > 0 || filesError?.length > 0

  return (
    <FormBody
      actionsProps={{
        ButtonAdd: false,
        ButtonClear: false,
        cancelProps: {
          onClick: handleCancel,
        },
        className: styles.actions,
        isSubmitting,
        submitProps: {
          isLoading: isSubmitting,
        },
        uploadProps: {
          onFinishUpload: handleUploadFile,
          onStartUpload: handleStartUploading,
          onUploadErrors: handleUploadErrors,
        },
      }}
    >
      <ContentComponent {...contentComponentProps} size={contentSize}>
        <FormInlineNotifications notifications={notifications} />

        <FormFieldsContainer className={styles.fieldsContainer}>
          <NameField />
          <EmailField />
          <PhoneField />
          <AddressField />
          <CategoryField />
          <BuildingField />
          <OpeningHoursField />
          <DescriptionField />
          {showFiles ? (
            <Field
              shouldAdaptToMobileScreen
              disabled={isSubmitting}
              label={`${t('Provider.AttachLabelKey')}:`}
            >
              <FileList
                errorItems={filesError}
                files={files}
                renderItem={renderFile}
                uploadingItems={filesUploading}
                onRemoveFile={handleRemoveFile}
              />
            </Field>
          ) : null}
          <FilesPreviewGallery
            files={files}
            initActive={activeFileIndex}
            scope={FILE_SCOPE_USER}
            onClose={handleCloseFilePreview}
          />
        </FormFieldsContainer>
      </ContentComponent>
    </FormBody>
  )
}

export default FormFields
